import {AfterViewInit, Component, TemplateRef, ViewChild} from '@angular/core';
import {Util} from "@data/util/util";
import {ProductPeriod, ResultsetThresholds} from "@data/interefaces/data.interfaces";
import {AgSidePanelComponent} from "@shared/ag-side-panel/ag-side-panel.component";
import {MatSort} from "@angular/material/sort";
import {ActivatedRoute} from "@angular/router";
import {ProductSpecService} from "@data/product-spec/product.spec.service";
import {TableBaseComponent} from "@pages/table.base.component";
import {QuilliupForm} from "@data/enums/quilliup.enum";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {FilterChangedEvent, SelectionChangedEvent, SelectionColumnDef, ViewportChangedEvent} from "@ag-grid-community/core";
import {marketCodes, pageContext, periods, ProductStatus, tableColumns} from "@pages/product-spec/product-period/product-period.component.ds";
import {LookupService} from "@data/lookup/lookup.service";
import {ReferenceService} from "@data/reference/reference.service";
import {YesNoValue} from "@data/enums/data.enums";
import {AgGridService} from "@shared/services/ag-grid.service";
import {FeatureComponentMode} from "@core/enums/core.enums";

@Component({
  selector: 'app-product-period',
  templateUrl: './product-period.component.html',
  styleUrls: ['./product-period.component.scss'],
  providers: [AgGridService]
})
export class ProductPeriodComponent extends TableBaseComponent implements AfterViewInit {
  isFilterActive: boolean = false;
  auxLinks: Map<string, string>;
  @ViewChild("productPeriodEdit") productPeriodEdit: AgSidePanelComponent | null = null;
  @ViewChild("languageEdit") languageEdit: AgSidePanelComponent | null = null;
  @ViewChild(MatSort, {static: false}) sort!: MatSort;

  showSkeleton: boolean = true;
  refreshComplete: boolean = true;
  kpiSidebar: KpiSidebarItem[] = [];
  rowData: ProductPeriod[] = [];
  selectedRows: ProductPeriod[] = [];
  isBulkOperationAllowed: boolean = false;
  selectedFeatureMode!: FeatureComponentMode;
  showTaskComponent: boolean = false;
  @ViewChild('footerActionButtonPanel') footerActionButtonPanel: TemplateRef<any> | undefined;

  protected override readonly tableColumns = tableColumns;
  protected override readonly pageContext = pageContext;
  protected readonly util = Util;

  protected readonly QuilliupForm = QuilliupForm;

  constructor(private _activatedRoute: ActivatedRoute, private _lookupService: LookupService,
              private _referenceService: ReferenceService, private _productSpecService: ProductSpecService) {
    super();
    this.auxLinks = new Map([['/home/welcome', 'Home']]);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initiatePageState(this._activatedRoute.snapshot.queryParamMap);
    this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
    this.loadFilterData();
    this.updateToPageContextUrl();
  }

  ngAfterViewInit() {
    this.displayProgressBar(true);
  }

  async refreshView() {
    return this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
  }

  loadFilterData() {
    this._referenceService.getAllMarkets().then(result => {
      result.forEach(item => marketCodes.push(item.marketCode));
    }).catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
    this._lookupService.getPeriods().then(result => {
      result.forEach(item => periods.push(item.period));
    }).catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
  }

  async loadPageData() {
    const period = this.pageContext.p_f || '';
    const marketCode = this.pageContext.mc_f || '';
    let thresholds: ResultsetThresholds | undefined;
    this.refreshComplete = false;
    this.displayProgressBar(true);
    await this._productSpecService.getProductPeriodThresholds(period, marketCode).then(result => {
      thresholds = result;
      this.setRowTemplateMessage(thresholds);
    }).catch(error => {
        this.displayNotificationMessage('error', JSON.stringify(error));
        this.displayProgressBar(false);
        this.refreshComplete = true;
      }
    );

    if (thresholds && !thresholds.hasTooMany) {
      await this._productSpecService.getProductPeriod(period, marketCode).then(result => {
        this.rowData = result;
      }).catch(error => this.displayNotificationMessage('error', JSON.stringify(error))
      ).finally(() => {
        this.refreshComplete = true;
        this.displayProgressBar(false);
      });
    } else {
      this.refreshComplete = true;
      this.rowData = [];
      this.displayProgressBar(false);
    }
  }

  onFilterChanged(event: FilterChangedEvent<any>) {
    const updateLiveDataFilter = this.isUpdateLiveDataFilterRequired(event.columns);
    this._agGridService.processOnFilterChanged(event, this.pageContext, this.updatePageContext.bind(this)).then(() => {
      if (updateLiveDataFilter) {
        this.onLiveDataFilterChange(true);
      }
    });
  }

  onLiveDataFilterChange(forceReload: boolean = false): void {
    if (forceReload) {
      this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
    }
    this.updateToPageContextUrl();
  }

  onRowSelectionChanged(event: SelectionChangedEvent) {
    this.refreshGridHeaderOnSelectionChanged(event);
    this.selectedRows = this.getSelectedRows();
    this.isBulkOperationAllowed = this.selectedRows.length != 0 && this.selectedRows.every(item => item.period === this.selectedRows[0].period &&
      item.marketCode === this.selectedRows[0].marketCode && item.industryCode === this.selectedRows[0].industryCode && item.surveySubject === this.selectedRows[0].surveySubject);
  }

  goBack() {
    this.showTaskComponent = false;
    this.refreshView();
  }

  onBulkEditClicked() {
    this.selectedFeatureMode = FeatureComponentMode.BULK_EDIT;
    this.showTaskComponent = true;
  }

  protected override updatePageContext(updateContextUrl: boolean = true): void {
    this.isFilterActive = Util.checkFilterActive(this.pageContext);
    if (updateContextUrl) {
      this.updateToPageContextUrl();
    }
    this.populateKpiMetrics();
  }

  protected override getSelectionColumnDef(): SelectionColumnDef {
    return this.getSelectColumnDef((params: any) => {
      return {
        showFlag: (params.data.pendingChecksumDatetime),
        flagIcon: "fa-exclamation",
        flagColor: "red",
        toolTip: 'Pending Checksum'
      }
    });
  }

  protected override getTableFooterActionButtonPanel(): TemplateRef<any> | undefined {
    return this.footerActionButtonPanel;
  }

  protected override onViewportChanged(event: ViewportChangedEvent<any>) {
    this.populateKpiMetrics();
  }

  private populateKpiMetrics() {
    let filteredRows = this.getRowsAfterFilter();
    let {includedCount, approvedCount} = this.calculateKpiMetrics(filteredRows);
    this.kpiSidebar = [
      {
        title: "Included",
        value: includedCount
      },
      {
        title: "Approved",
        value: approvedCount
      }
    ];
  }

  private calculateKpiMetrics(rows: ProductPeriod[]) {
    let includedCount = rows.filter(element => element != null && element.include === YesNoValue.YES_VALUE).length;
    let approvedCount = rows.filter(element => element != null && element.approvalStatus === ProductStatus.APPROVED).length;
    return {includedCount, approvedCount};
  }
}
